import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { connectMQTTThunk } from '../redux/thunks/MQTTThunks';
import { userSelectOneObjectByQuery } from '../redux/thunks/DBServiceThunks';

/**
 * Centralized MQTT connection management hook
 * Prevents multiple components from competing for MQTT connections
 */
export const useMQTTConnection = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));
  const mqttState = useAppSelector(state => state.mqtt);
  
  // Track if we've already initiated a connection attempt for this user session
  const connectionInitiated = useRef<string | null>(null);
  const connectionAttemptTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const userId = currentUser?._id;
    
    // Only proceed if we have a user
    if (!userId) {
      connectionInitiated.current = null;
      return;
    }

    // Check if we've already initiated connection for this user
    if (connectionInitiated.current === userId) {
      return;
    }

    // Check if already connected or connecting
    if (mqttState.isConnected || mqttState.isConnecting) {
      connectionInitiated.current = userId;
      return;
    }

    // Check circuit breaker status before attempting connection
    const mqttSubscriber = (window as any).MQTTSubscriber?.getInstance?.();
    const connectionStatus = mqttSubscriber?.getConnectionStatus?.();
    
    if (connectionStatus?.isCircuitBreakerOpen) {
      console.log('[useMQTTConnection] Circuit breaker is open, delaying connection attempt for', 
        connectionStatus.timeUntilNextAttempt, 'seconds');
      
      // Schedule retry after circuit breaker timeout
      if (connectionAttemptTimeout.current) {
        clearTimeout(connectionAttemptTimeout.current);
      }
      
      connectionAttemptTimeout.current = setTimeout(() => {
        connectionInitiated.current = null; // Reset to allow retry
      }, connectionStatus.timeUntilNextAttempt * 1000);
      
      return;
    }

    // Mark connection as initiated for this user
    connectionInitiated.current = userId;
    
    console.log('[useMQTTConnection] Initiating centralized MQTT connection for user:', userId);
    
    // Dispatch connection attempt
    dispatch(connectMQTTThunk({ userId })).catch((error) => {
      console.error('[useMQTTConnection] Connection failed:', error);
      // Reset connection initiated flag on failure to allow retry
      connectionInitiated.current = null;
    });

    // Cleanup timeout on unmount or user change
    return () => {
      if (connectionAttemptTimeout.current) {
        clearTimeout(connectionAttemptTimeout.current);
        connectionAttemptTimeout.current = null;
      }
    };
  }, [currentUser?._id, mqttState.isConnected, mqttState.isConnecting, dispatch]);

  // Reset connection state when user changes
  useEffect(() => {
    const userId = currentUser?._id;
    if (!userId && connectionInitiated.current) {
      console.log('[useMQTTConnection] User logged out, resetting connection state');
      connectionInitiated.current = null;
      if (connectionAttemptTimeout.current) {
        clearTimeout(connectionAttemptTimeout.current);
        connectionAttemptTimeout.current = null;
      }
    }
  }, [currentUser?._id]);

  return {
    isConnected: mqttState.isConnected,
    isConnecting: mqttState.isConnecting,
    connectionError: mqttState.connectionError,
    userId: mqttState.userId,
    clientId: mqttState.clientId
  };
}; 